import React from "react";

import { BROKER_ALERT_CLASS, BROKER_ALERT_DESC_CLASS } from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { useActiveBrokerAccount } from "../../hooks/useActiveBrokerAccount";
import { useInstrumentSetup } from "../../contexts/InstrumentSetupContext";
import { accountNeedsInstrumentSetup } from "../../utils/instrumentMarketDataReadiness";

export function InstrumentSetupBanner(): React.ReactElement | null {
  const { activeAccountCode, accounts } = useActiveBrokerAccount();
  const { openSetup, setupData } = useInstrumentSetup();

  const activeAccount = accounts.find((a) => a.accountCode === activeAccountCode) ?? null;
  // Layer A only — disconnected OHLC (chart_available: false) must not trigger this banner.
  const blocking =
    setupData != null
      ? accountNeedsInstrumentSetup(setupData)
      : Boolean(activeAccount?.instrumentSetupPending);
  if (!activeAccount || !blocking) {
    return null;
  }

  return (
    <Alert variant="default" className={BROKER_ALERT_CLASS}>
      <Info className="size-4 text-[color:var(--brand-tropical-surf)]" />
      <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
        <div className="flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-center">
          <p className="m-0 text-sm text-muted-foreground">
            Instrument market data setup is required for{" "}
            <strong className="font-medium text-foreground">
              {activeAccount.tradeLabel || activeAccount.accountCode}
            </strong>
            .
          </p>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={() => {
              if (activeAccountCode) {
                void openSetup(activeAccountCode, { mandatory: true });
              }
            }}
          >
            Configure
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
